import { useEffect, useRef, useState } from 'react';
import { DateRangePicker } from 'rsuite';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  toggleAxesEdited,
  updateAxesQueryParam,
} from '../../../redux/ChartPoperties/ChartPropertiesActions';
import {
  setParameterData,
  setParameterIntoFilters,
} from '../../../redux/Parameters/ParametersAction';
import { RootState } from '../../../redux';
import { ParameterProps } from '../ParameterContainer';
import { chartFilterGroupEdited } from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { updateCalculation } from '../../../redux/Calculations/CalculationsActions';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { AxisType } from '../../CommonFunctions/aliases';

const CalendarRange = ({
  parameter,
  handleEditParameter,
  propKey,
  editMode,
  showDash,
  color,
  backgroundColor,
}: ParameterProps) => {
  const { chartProperties, calculations } = useSelector((state: RootState) => {
    return {
      chartProperties: state.chartProperties,
      calculations: state.calculations,
    };
  });
  const dispatch = useDispatch();

  const [value, setValue] = useState<Date[]>(() => {
    return parameter.userSelection
      ? [new Date(parameter.userSelection[0]), new Date(parameter.userSelection[1])]
      : [new Date(parameter.minValue), new Date(parameter.maxValue)];
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(true);

  const currentChartAxesRef = useRef<any[]>([]);
  const currentChatFieldRef = useRef<any>(null);
  const currentChartAxesNameRef = useRef<any>(null);
  const itemIndexRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const currentChartField = chartProperties.properties[propKey]?.chartAxes[0]?.fields?.find(
    (field: any) => field.parameterId === parameter.parameterId,
  );

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const callQueryAPIFilter = () => {
    let callAPIDashboard = false;
    chartProperties.propList[1].forEach((tabTileId: string) => {
      chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
        if (field.parameterId === parameter.parameterId) {
          dispatch(callQueryAPI(tabTileId, true));
          callAPIDashboard = true;
        }
      });
      let measureAxes = chartProperties.properties[tabTileId].chartAxes.filter((axis: any) =>
        [AxisType.Measure, AxisType.X, AxisType.Y].includes(axis.name),
      );
      measureAxes.forEach((axes: any) => {
        axes.fields.some((field: any) => {
          if (field.override) {
            field.override[0].fields.some((FilterField: any) => {
              if (FilterField.parameterId === parameter.parameterId) {
                dispatch(callQueryAPI(tabTileId, true));
                callAPIDashboard = true;
              }
            });
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const callAPI = (uid: string | undefined) => {
    let callAPIDashboard = false;
    Object.entries(chartProperties.properties).forEach(([tabTileId, prop]: [string, any]) => {
      prop.chartAxes?.forEach((axis: any) => {
        axis.fields?.forEach((field: any) => {
          if (field.SavedCalculationUUID === uid) {
            dispatch(callQueryAPI(tabTileId, true));
            callAPIDashboard = true;
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const handleChangeDate = (date: Date[]) => {
    const dates = [date[0].toISOString().slice(0, 10), date[1].toISOString().slice(0, 10)];
    setValue(date);
    dispatch(setParameterData(parameter.parameterId, dates));

    // if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
    //   Object.defineProperty(currentChatFieldRef.current, 'greaterThanOrEqualTo', {
    //     value: dates[0],
    //     writable: true,
    //     enumerable: true,
    //     configurable: true,
    //   });
    //   Object.defineProperty(currentChatFieldRef.current, 'lessThanOrEqualTo', {
    //     value: dates[1],
    //     writable: true,
    //     enumerable: true,
    //     configurable: true,
    //   });

    //   updateUserFilterItem(
    //     propKey,
    //     0,
    //     itemIndexRef.current,
    //     currentChatFieldRef.current,
    //     currentChartAxesNameRef.current,
    //   );
    // }

    Array.isArray(parameter.chartFilter) &&
      parameter.chartFilter.forEach((cf: any) => {
        if (!cf.currentChartAxesName?.includes('chartAxes_')) {
          if (cf.propKey) {
            const clonedField = {
              ...chartProperties.properties[cf.propKey].chartAxes[0].fields[cf.itemIndex],
            };
            Object.defineProperty(clonedField, 'greaterThanOrEqualTo', {
              value: dates[0],
              writable: true,
              enumerable: true,
              configurable: true,
            });
            Object.defineProperty(clonedField, 'lessThanOrEqualTo', {
              value: dates[1],
              writable: true,
              enumerable: true,
              configurable: true,
            });
            updateUserFilterItem(cf.propKey, 0, cf.itemIndex, clonedField, cf.currentChartAxesName);
          } else if (cf.itemIndex >= 0 && cf.currentChartAxesName) {
            chartProperties.propList[1].forEach((tabTileId: string) => {
              chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
                if (field.parameterId === parameter.parameterId) {
                  dispatch(
                    setParameterIntoFilters(
                      tabTileId,
                      parameter.parameterId,
                      field.uId,
                      cf.itemIndex,
                      cf.currentChartAxesName,
                    ),
                  );

                  const clonedField = {
                    ...chartProperties.properties[tabTileId].chartAxes[0].fields[cf.itemIndex],
                  };
                  Object.defineProperty(clonedField, 'greaterThanOrEqualTo', {
                    value: dates[0],
                    writable: true,
                    enumerable: true,
                    configurable: true,
                  });
                  Object.defineProperty(clonedField, 'lessThanOrEqualTo', {
                    value: dates[1],
                    writable: true,
                    enumerable: true,
                    configurable: true,
                  });
                  updateUserFilterItem(
                    tabTileId,
                    0,
                    cf.itemIndex,
                    clonedField,
                    cf.currentChartAxesName,
                  );
                }
              });
            });
          }
        }
      });
    callQueryAPIFilter();

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      filter.rightOperand[0] = dates[0];
                      filter.rightOperand[1] = dates[1];
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
            callAPI(calculation.uuid);
          }
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        if (filter.propKey) {
          const chartAxes = chartProperties.properties[propKey]?.chartAxes;
          const chartFilter = chartAxes[chartAxes.length - 1].fields;
          chartFilter?.forEach((field: any, index: number) => {
            if (currentChartAxesName_ === field.enableOverrideForUID) {
              return;
            }
            if (field.uId === currentChartAxesName_) {
              const override_ = field.override;
              if (Array.isArray(override_) && override_.length > 0) {
                override_[0].fields.forEach((field_: any, index_: number) => {
                  if (field_.uId === filter.chartFilterId) {
                    Object.defineProperty(field_, 'greaterThanOrEqualTo', {
                      value: dates[0],
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });
                    Object.defineProperty(field_, 'lessThanOrEqualTo', {
                      value: dates[1],
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });

                    field.override = override_;
                    updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                    callQueryAPIFilter();
                  }
                });
              }
            }
          });
        } else {
        }
      }
    });
  };

  useEffect(() => {
    currentChartAxesRef.current = chartProperties.properties[propKey]?.chartAxes[0]?.fields || [];

    // Find the field where any chartFilter in parameter.chartFilter matches filter.uId
    currentChatFieldRef.current = currentChartAxesRef.current.find(
      (filter: any) =>
        Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.some((cf: any) => {
          if (cf.chartFilterId === filter.uId) {
            currentChartAxesNameRef.current = cf.currentChartAxesName;
            itemIndexRef.current = cf.itemIndex;
            return cf.chartFilterId === filter.uId;
          }
        }),
    );
  }, [
    chartProperties.properties[propKey]?.chartAxes[0]?.fields,
    parameter.chartFilter,
    propKey,
    currentChartField?.parameterId,
  ]);

  // if the parameter had any default value then it will update the Graph
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const value1 = value[0].toISOString().slice(0, 10);
    const value2 = value[1].toISOString().slice(0, 10);
    if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
      Object.defineProperty(currentChatFieldRef.current, 'greaterThanOrEqualTo', {
        value: value1,
        writable: true,
        enumerable: true,
        configurable: true,
      });
      Object.defineProperty(currentChatFieldRef.current, 'lessThanOrEqualTo', {
        value: value2,
        writable: true,
        enumerable: true,
        configurable: true,
      });

      updateUserFilterItem(
        propKey,
        0,
        itemIndexRef.current,
        currentChatFieldRef.current,
        currentChartAxesNameRef.current,
      );
      callQueryAPIFilter();
    }

    if (parameter.calculationId.length > 0) {
      const targetCalculation = calculations.savedCalculations.filter(
        (cal: any) => cal.uuid === parameter.calculationId.find((id: any) => id === cal.uuid),
      );
      targetCalculation.map((calculation: any) => {
        const targetFlowNames = Object.keys(calculation.calculationInfo.flows);
        targetFlowNames.map((flowName: any) => {
          const targetFlow = calculation.calculationInfo.flows[flowName][0];
          const conditionFilters = calculation.calculationInfo.conditionFilters;
          if (targetFlow.flow === 'IfElse') {
            for (const conditionFilter in conditionFilters) {
              let newConditions = conditionFilters[conditionFilter][0];
              newConditions.conditions.forEach((filter: any) => {
                if (filter.parameterId.length > 0) {
                  filter.parameterId.map((parameterId: string, index: number) => {
                    if (parameterId === parameter.parameterId) {
                      filter.rightOperand[0] = value[0];
                      filter.rightOperand[1] = value[1];
                    }
                  });
                }
              });
            }
            dispatch(toggleAxesEdited(propKey, true));
            dispatch(chartFilterGroupEdited(true));
            dispatch(updateCalculation(calculation));
          }
        });
      });
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        const chartAxes = chartProperties.properties[propKey]?.chartAxes;
        const chartFilter = chartAxes[chartAxes.length - 1].fields;
        chartFilter?.forEach((field: any, index: number) => {
          if (currentChartAxesName_ === field.enableOverrideForUID) {
            return;
          }
          if (field.uId === currentChartAxesName_) {
            const override_ = field.override;
            if (Array.isArray(override_) && override_.length > 0) {
              override_[0].fields.forEach((field_: any, index_: number) => {
                if (field_.uId === filter.chartFilterId) {
                  Object.defineProperty(field_, 'greaterThanOrEqualTo', {
                    value: value1,
                    writable: true,
                    enumerable: true,
                    configurable: true,
                  });
                  Object.defineProperty(field_, 'lessThanOrEqualTo', {
                    value: value2,
                    writable: true,
                    enumerable: true,
                    configurable: true,
                  });

                  field.override = override_;
                  updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                  callQueryAPIFilter();
                }
              });
            }
          }
        });
      }
    });
  }, [
    parameter.chartFilter,
    // currentChatFieldRef.current,
    // propKey
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              transform: 'capitalize',
              color: color,
              textAlign: 'start',
            }}
          >
            {parameter?.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: -1,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {editMode && (
            <IconButton
              size="small"
              id="basic-button"
              aria-controls={Boolean(anchorEl) ? 'basic-menu' : undefined}
              sx={{ color: color }}
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
          )}
          <IconButton
            size="small"
            sx={{
              mr: '0.4rem',
              color: color,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem dense onClick={() => handleEditParameter(parameter.parameterId)}>
          Remove
        </MenuItem>
      </Menu>
      <Box sx={{ width: '100%', position: 'relative' }}>
        <style>
          {`
        .rs-input-group.rs-input-group-inside{
          background: ${backgroundColor} !important;
         border: 1px solid ${color} !important;
          }
          .rs-picker-daterange > .rs-input-group.rs-input-group-inside .rs-input {
           background: ${backgroundColor} !important;
           color: ${color} !important;
          }

            .custom-calendar-range .rs-input-group.rs-input-group-inside {
             background: ${backgroundColor} !important;
             border: 1px solid ${color} !important;
           }

           .rs-input-group.rs-input-group-inside .rs-input-group-addon{
            color: ${color} !important;
           }
    `}
        </style>
        {expanded && (
          <DateRangePicker
            size="sm"
            placeholder="Select a date range"
            style={{
              width: '97%',
            }}
            container={() => document.querySelector('.MuiDialog-root')!}
            placement="leftStart"
            value={[value[0], value[1]]}
            onChange={(date) => handleChangeDate(date!)}
            cleanable={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default CalendarRange;
